import Vue from 'vue'
import isEmpty from 'lodash/isEmpty'
import AuthAPI from '@/api/auth'
import AuthV2API from '@/api/v2/auth'
import ProfileAPI from '@/api/profile'
import DeepIntegrationAPI from '@/api/deep-integration'
import AUTH_SCREENS from '@/constants/auth-screens'
import {
  getToken,
  setToken,
  setUserId,
  removeToken,
  removeUserId,
  setLoginSource,
  getLoginSource,
  removeLoginSource,
  getUserRolesFromToken,
} from '@/utils/auth'
import { setFingerprint, getFingerprint } from '@/utils/fingerprint'
import { getSharedCredentialsByOtherTamaraApps } from '@/utils/share-token'
import logger from '@/utils/logger'
import clientLogger from '@/plugins/logger/core'
import AccountRecoveryAPI from '@/api/account-recovery'
import { HttpRequestError } from '@/utils/error'
import { getTrackingKey } from '@/utils/api'
import { parseCustomerPhoneForAuth } from '@/utils/phone-number'

const AUTH_V2_BASE_URI = '/auth/v1/customers'

const state = {
  screen: AUTH_SCREENS.input_phone,
  accessToken: getToken(),
  customer: {},
  phoneNumber: {
    phoneNumber: '',
    countryCode: '',
  },
  confirming: false,
  authenticated: false,
  authenticating: false,
  tempAuthenticating: false,
  isDormantCustomer: false,
  inAppChallengeRequired: false,
  error: null,
  autoSubmitPhone: false,
  loginSource: getLoginSource() || 'customer',
  shouldValidateSharedToken: !isEmpty(getSharedCredentialsByOtherTamaraApps()),
  validatingSharedToken: false,
  changePhoneNumberClicked: false,
  profileLoading: false,
  profileLastFetched: null,
  profileError: null,
  maskedNationalId: '',
  showMaxAttemptsScreen: false,
  merchantFailureUrl: '',
  fingerprint: getFingerprint() || '',
}

const getters = {
  loggedIn(state) {
    return !!state.accessToken
  },
  currentCustomer(state) {
    return state.customer || {}
  },
  isDormantCustomer(state) {
    return state.isDormantCustomer
  },
  inAppChallengeRequired(state) {
    return state.inAppChallengeRequired
  },
  firstName(_, getters) {
    return getters.currentCustomer?.firstName
  },
  customerType(state) {
    const isNewCustomer = state.customer?.isNewCustomer

    if (!TypeChecker.isBoolean(isNewCustomer)) {
      return ''
    }

    return isNewCustomer ? 'new' : 'recurrent'
  },
  customerId(state) {
    return state.customer?.customerId
  },
  customerPhone(state) {
    return state.phoneNumber
  },
  customerEmail(state) {
    return state.customer?.email
  },
  customerPhoneNumber(state) {
    return state.customer?.phoneNumber
  },
  isIdVerified(state) {
    return state.customer?.isIdVerified
  },
  isoCode2() {
    return state.customer?.countryCode
  },
  isPnOptedIn() {
    return state.customer?.isPnOptedIn
  },
  otpChannels() {
    return state.customer?.channels
  },
  isProfileLoading(state) {
    return state.profileLoading
  },
  profileLastFetched(state) {
    return state.profileLastFetched
  },
  fingerprint(state) {
    return state.fingerprint
  },
  isProfileFresh:
    (state) =>
    (maxAge = 180000) => {
      if (
        !state.profileLastFetched ||
        !state.customer ||
        Object.keys(state.customer).length === 0
      ) {
        return false
      }
      return Date.now() - state.profileLastFetched < maxAge
    },
  maskedNationalId(state) {
    return state.maskedNationalId
  },
  showMaxAttemptsScreen(state) {
    return state.showMaxAttemptsScreen
  },
  merchantFailureUrl(state) {
    return state.merchantFailureUrl
  },
  currentScreen(state) {
    return state.screen
  },
}

const mutations = {
  INITIAL_PAGE(state, { screen, phoneNumber }) {
    state.screen = screen
    state.phoneNumber = { ...phoneNumber }
  },
  SET_SCREEN(state, nextScreen) {
    state.screen = nextScreen
  },
  SET_ACCESS_TOKEN: (state, token) => {
    state.accessToken = token
  },
  SET_CUSTOMER: (state, customer = {}) => {
    const currentCustomer = state.customer || {}

    state.customer = { ...currentCustomer, ...customer }
  },
  SET_PHONE_NUMBER(state, phoneNumber) {
    state.phoneNumber = { ...phoneNumber }
  },
  SET_STATUS(state, { name, status }) {
    state[name] = status
  },
  SET_AUTO_SUBMIT_PHONE(state, value) {
    state.autoSubmitPhone = value
  },
  SET_LOGIN_SOURCE(state, value) {
    state.loginSource = value
  },
  SET_CHANGE_PHONE_NUMBER_CLICKED: (state, value) => {
    state.changePhoneNumberClicked = value
  },
  SET_PROFILE_LOADING: (state, loading) => {
    state.profileLoading = loading
  },
  SET_PROFILE_LAST_FETCHED: (state, timestamp) => {
    state.profileLastFetched = timestamp
  },
  SET_PROFILE_ERROR: (state, error) => {
    state.profileError = error
  },
  CLEAR_PROFILE_ERROR: (state) => {
    state.profileError = null
  },
  SET_MASKED_NATIONAL_ID: (state, value) => {
    state.maskedNationalId = value
  },
  SET_SHOW_MAX_ATTEMPTS_SCREEN: (state, value) => {
    state.showMaxAttemptsScreen = value
  },
  SET_MERCHANT_FAILURE_URL: (state, value) => {
    state.merchantFailureUrl = value
  },
  SET_FINGERPRINT: (state, value) => {
    state.fingerprint = value
    setFingerprint(value)
  },
  SET_IN_APP_CHALLENGE_REQUIRED: (state, value) => {
    state.inAppChallengeRequired = value
  },
}

const actions = {
  setPhone({ commit }, phone) {
    commit('SET_PHONE_NUMBER', phone)
  },
  setStatus({ commit }, { name, status }) {
    commit('SET_STATUS', { name, status })
  },
  // Request authenticate
  async authenticate({ dispatch, commit, state, rootGetters }, data) {
    try {
      commit('SET_STATUS', { name: 'authenticating', status: true })
      commit('SET_PHONE_NUMBER', data)

      const dataWillBeSent = {
        phoneNumber: data.phoneNumber,
        countryCode: data.countryCode,
        checkoutId: rootGetters['session/checkoutId'],
        orderId: data.orderId,
      }

      if (!dataWillBeSent.checkoutId) {
        logger.debug(
          'Empty checkout ID',
          {
            data: { ...dataWillBeSent },
          },
          { category: 'authenticate' }
        )

        clientLogger.debug('Empty checkout ID')

        dataWillBeSent.checkoutId = data.checkoutId // Use fallback checkoutId

        dispatch('session/setCheckoutId', dataWillBeSent.checkoutId, {
          root: true,
        })
      }

      logger.debug(
        'Sending authenticate request',
        {
          data: { ...dataWillBeSent },
        },
        { category: 'authenticate' }
      )

      clientLogger.debug('Sending authenticate request')

      let response = {}
      let customer = {}
      const isAuthenticationServiceLoginEnabled =
        rootGetters['session/authenticationServiceLoginEnabled']

      if (isAuthenticationServiceLoginEnabled) {
        const newDataToBeSent = {
          phoneNumber: '+' + data.countryCode + data.phoneNumber,
          checkoutId: rootGetters['session/checkoutId'],
        }
        response = await AuthV2API.sendOtp(newDataToBeSent)

        // handle new endpoints error response
        if (response?.data?.status === 'ERROR') {
          commit('SET_STATUS', { name: 'authenticating', status: false })
          const errorData = response?.data?.data || {}
          // Ensure screenType is set from type if present (for error-handler mixin compatibility)
          const dataWithScreenType = {
            ...errorData,
            screenType: errorData.screenType || errorData.type,
          }
          throw new HttpRequestError(
            errorData.message || 'Error occurred',
            {
              status: 400,
              data: dataWithScreenType,
              config: { url: `${AUTH_V2_BASE_URI}/send-otp` },
            },
            getTrackingKey(AUTH_V2_BASE_URI, 'send-otp')
          )
        }
        customer = response?.data?.data
        customer = {
          ...customer,
          code: customer.otp,
        }
      } else {
        response = await AuthAPI.authenticate(dataWillBeSent)
        customer = response?.data
      }

      commit('SET_CUSTOMER', customer)
      const phoneParts = parseCustomerPhoneForAuth(customer)
      if (phoneParts.countryCode && phoneParts.phoneNumber) {
        commit('SET_PHONE_NUMBER', phoneParts)
      }
      commit('SET_STATUS', { name: 'authenticating', status: false })

      logger.debug('Authenticate response', {}, { category: 'authenticate' })
      clientLogger.debug('Authenticate response')

      return response
    } catch (err) {
      commit('SET_STATUS', { name: 'authenticating', status: false })

      throw err
    } finally {
      if (state.autoSubmitPhone) {
        dispatch('setAutoSubmitPhone', false)
      }
    }
  },

  // Request authenticate by temporary token for deep integration
  async authenticateByCheckoutId({ commit }, checkoutId) {
    try {
      commit('SET_STATUS', { name: 'tempAuthenticating', status: true })
      const response = await DeepIntegrationAPI.authenticateByCheckoutId(
        checkoutId
      )

      const { data } = response
      const { token = '' } = data || {}

      commit('SET_ACCESS_TOKEN', token)
      commit('SET_STATUS', { name: 'tempAuthenticating', status: false })

      setToken(token)

      return response
    } catch (err) {
      commit('SET_STATUS', { name: 'tempAuthenticating', status: false })

      throw err
    }
  },

  // Confirm OTP|PIN
  async confirm({ commit, dispatch, getters, rootGetters }, data) {
    try {
      commit('SET_STATUS', { name: 'confirming', status: true })

      // Defensive check: ensure customerId is present
      if (!data?.customerId) {
        logger.error(
          'Missing customerId in confirm action',
          { data },
          { category: 'authenticate' }
        )
        throw new Error(
          'Customer ID is required for authentication confirmation'
        )
      }

      if (!data?.checkoutId) {
        data.checkoutId = rootGetters['session/checkoutId']
      }

      logger.debug(
        'Confirm authenticate request',
        {},
        { category: 'authenticate' }
      )

      clientLogger.debug('Confirm authenticate request')

      let response = {}
      const isAuthenticationServiceLoginEnabled =
        rootGetters['session/authenticationServiceLoginEnabled']

      let token, fingerprint
      if (isAuthenticationServiceLoginEnabled) {
        const newDataToBeSent = {
          code: data.code,
          customerId: data.customerId,
          checkoutId: data.checkoutId,
          fingerprint: data.fingerprint,
        }
        response = await AuthV2API.confirmOtp(newDataToBeSent)
        // handle new endpoints error response
        if (response?.data?.status === 'ERROR') {
          commit('SET_STATUS', { name: 'confirming', status: false })
          const errorData = response?.data?.data || {}
          // Ensure screenType is set from type if present (for error-handler mixin compatibility)
          const dataWithScreenType = {
            ...errorData,
            screenType: errorData.screenType || errorData.type,
          }
          throw new HttpRequestError(
            errorData.message || 'Error occurred',
            {
              status: 400,
              data: dataWithScreenType,
              config: { url: `${AUTH_V2_BASE_URI}/confirm-otp` },
            },
            getTrackingKey(AUTH_V2_BASE_URI, 'confirm-otp')
          )
        }
        token = response?.data?.data?.token
        fingerprint = response?.data?.data?.fingerprint
      } else {
        response = await AuthAPI.confirm(data)
        token = response?.data?.token
        fingerprint = response?.data?.fingerprint
      }

      const { customerId } = getters.currentCustomer
      const roles = getUserRolesFromToken(token)
      const dormantRole = 'ROLE_DORMANT_CUSTOMER'
      const isDormantCustomer = roles?.includes(dormantRole)
      // Set screen account recovery if account recovery is needed, Don't set token if account recovery is needed, but always set userId
      commit('SET_STATUS', {
        name: 'isDormantCustomer',
        status: isDormantCustomer,
      })
      commit('SET_STATUS', { name: 'authenticated', status: false })
      commit('SET_ACCESS_TOKEN', token)
      setToken(token)
      setUserId(customerId)

      if (fingerprint && fingerprint !== getters.fingerprint) {
        commit('SET_FINGERPRINT', fingerprint)
      }

      commit('SET_STATUS', { name: 'confirming', status: false })

      logger.debug(
        'Confirm authenticate response OK',
        {},
        { category: 'authenticate' }
      )

      clientLogger.debug('Confirm authenticate response OK')

      if (isDormantCustomer) {
        dispatch('changeToScreen', AUTH_SCREENS.account_recovery)
      }
      return response
    } catch (err) {
      commit('SET_STATUS', { name: 'confirming', status: false })

      throw err
    }
  },
  setCustomer({ commit }, customer = {}) {
    commit('SET_CUSTOMER', customer)
  },
  finishMfaLogin({ commit, getters }, { token }) {
    const customerId = getters.customerId

    commit('SET_STATUS', { name: 'authenticated', status: false })
    commit('SET_ACCESS_TOKEN', token)
    setToken(token)
    setUserId(customerId)

    commit('SET_STATUS', { name: 'confirming', status: false })

    logger.debug('MFA otp login response OK', {}, { category: 'mfa' })
  },

  // Validates the current user's token and refreshes it with new data from the API.
  async getProfile(
    { commit, dispatch, state, getters },
    { checkoutId = '', force = false, maxAge = 180000 } = {}
  ) {
    // Clear any previous errors
    commit('CLEAR_PROFILE_ERROR')

    // Check if we have a valid access token
    if (!state.accessToken) {
      return null
    }

    // When force=true, check if data is very recent (within 10 seconds)
    if (force) {
      // Even with force=true, use cache if data is less than 10 seconds old
      const thirtySecondsAgo = 10000 // 10 seconds in milliseconds
      if (getters.isProfileFresh(thirtySecondsAgo)) {
        return state.customer
      }
    } else {
      // Use smart caching: check if profile is fresh when not forcing
      if (getters.isProfileFresh(maxAge)) {
        return state.customer
      }
    }

    // Prevent multiple simultaneous calls (but allow force to override)
    if (state.profileLoading && !force) {
      // Wait for the current loading to complete
      return new Promise((resolve) => {
        const checkLoading = () => {
          if (!state.profileLoading) {
            resolve(state.customer)
          } else {
            setTimeout(checkLoading, 100)
          }
        }
        checkLoading()
      })
    }

    commit('SET_PROFILE_LOADING', true)

    try {
      const { data } = await ProfileAPI.getProfile({ checkoutId })

      // Update state with fresh data
      commit('SET_CUSTOMER', data)
      const phoneParts = parseCustomerPhoneForAuth(data)
      if (phoneParts.countryCode && phoneParts.phoneNumber) {
        commit('SET_PHONE_NUMBER', phoneParts)
      }
      commit('SET_PROFILE_LAST_FETCHED', Date.now())

      return data
    } catch (error) {
      // Store the error for debugging
      commit('SET_PROFILE_ERROR', error)

      // Only reset auth for certain error types (401, 403)
      if (error.response && [401, 403].includes(error.response.status)) {
        dispatch('resetAuth')
      }

      // If we have cached data and this isn't a forced refresh, return cached data
      if (!force && state.customer && Object.keys(state.customer).length > 0) {
        return state.customer
      }

      throw error
    } finally {
      commit('SET_PROFILE_LOADING', false)
    }
  },

  // Trigger resend OTP code
  async resendOTP({ commit, getters, rootGetters }, dataToBeSent) {
    const { customerId } = getters.currentCustomer

    logger.debug('Resend OTP request', {}, { category: 'authenticate' })
    clientLogger.debug('Resend OTP request')

    const isAuthenticationServiceLoginEnabled =
      rootGetters['session/authenticationServiceLoginEnabled']

    let response, code
    if (isAuthenticationServiceLoginEnabled) {
      response = await AuthV2API.resendOtp({
        checkoutId: dataToBeSent.checkoutId,
        customerId,
      })
      // handle new endpoints error response
      if (response?.data?.status === 'ERROR') {
        commit('SET_STATUS', { name: 'authenticating', status: false })
        const errorData = response?.data?.data || {}
        // Ensure screenType is set from type if present (for error-handler mixin compatibility)
        const dataWithScreenType = {
          ...errorData,
          screenType: errorData.screenType || errorData.type,
        }
        throw new HttpRequestError(
          errorData.message || 'Error occurred',
          {
            status: 400,
            data: dataWithScreenType,
            config: { url: `${AUTH_V2_BASE_URI}/resend-otp` },
          },
          getTrackingKey(AUTH_V2_BASE_URI, 'resend-otp')
        )
      }
      const data = response?.data?.data
      code = data?.otp
    } else {
      response = await AuthAPI.resendOTP({ customerId, ...dataToBeSent })
      const { data } = response
      code = data?.code
    }

    commit('SET_CUSTOMER', { code })

    logger.debug('Resend OTP response', {}, { category: 'authenticate' })
    clientLogger.debug('Resend OTP response')

    return response
  },

  handleUpdateLocaleIfNeeded({ rootGetters }, userLocale = '') {
    const applicationLocale = rootGetters['application/currentLocale']

    // Call the API update user locale when it is different from the current application locale
    if (userLocale !== applicationLocale) {
      ProfileAPI.setLocale(applicationLocale)
    }
  },

  setCustomerToken({ commit, getters }, { token, source = 'customer' }) {
    const { customerId } = getters.currentCustomer

    commit('SET_ACCESS_TOKEN', token)
    commit('SET_LOGIN_SOURCE', source)

    setToken(token)
    setUserId(customerId)
    setLoginSource(source)
  },

  changeToScreen({ commit }, nextScreen) {
    if (!Object.values(AUTH_SCREENS).includes(nextScreen)) {
      throw new Error(`[${nextScreen}] is not a valid auth screen`)
    }

    commit('SET_SCREEN', nextScreen)
  },

  setAutoSubmitPhone({ commit }, value) {
    commit('SET_AUTO_SUBMIT_PHONE', value)
  },

  /** Ensures `autoSubmitPhone` watchers see a false→true transition (e.g. login language switch). */
  async pingAutoSubmitPhone({ dispatch }) {
    await dispatch('setAutoSubmitPhone', false)
    await Vue.nextTick()
    await dispatch('setAutoSubmitPhone', true)
  },

  // remove token
  resetAuth({ commit }) {
    commit('SET_ACCESS_TOKEN', '')
    commit('SET_LOGIN_SOURCE', '')
    commit('SET_AUTO_SUBMIT_PHONE', false)
    commit('SET_PROFILE_LOADING', false)
    commit('SET_PROFILE_LAST_FETCHED', null)
    commit('CLEAR_PROFILE_ERROR')

    removeToken()
    removeUserId()
    removeLoginSource()
  },

  cleanUp({ dispatch }) {
    dispatch('resetAuth')
    dispatch('setPhone', { phoneNumber: '', countryCode: '' })
    dispatch('changeToScreen', AUTH_SCREENS.input_phone)
  },
  setChangePhoneNumberClicked({ commit }, value) {
    commit('SET_CHANGE_PHONE_NUMBER_CLICKED', value)
  },

  // Verify account recovery with ID number
  async reactivateAccount({ commit, getters, rootGetters }, { idNumber }) {
    try {
      const { customerId } = getters.currentCustomer
      const checkoutId = rootGetters['session/checkoutId']

      logger.debug(
        'Verify account recovery request',
        {},
        { category: 'account-recovery' }
      )

      const response = await AccountRecoveryAPI.reactivateAccount({
        customerId,
        idNumber,
        checkoutId,
      })

      // Set token only if verification was successful
      const { status } = response.data || {}
      const { token } = response.data?.data || {}

      if (token && status === 'SUCCESS') {
        commit('SET_ACCESS_TOKEN', token)
        setToken(token)
        setUserId(customerId)
        commit('SET_LOGIN_SOURCE', 'customer')
      }

      logger.debug(
        'Verify account recovery response OK',
        {},
        { category: 'account-recovery' }
      )

      return response
    } catch (err) {
      logger.error('Verify account recovery failed', err, {
        category: 'account-recovery',
      })
      throw err
    }
  },

  // Create new account for account recovery
  async createNewAccount({ commit, getters, rootGetters }) {
    try {
      const { customerId } = getters.currentCustomer
      const checkoutId = rootGetters['session/checkoutId']

      logger.debug(
        'Create new account request',
        {},
        { category: 'account-recovery' }
      )

      const response = await AccountRecoveryAPI.createNewAccount({
        customerId,
        checkoutId,
      })

      // Set token only if new account creation was successful
      const { status } = response.data || {}
      const { token } = response.data?.data || {}

      if (token && status === 'SUCCESS') {
        commit('SET_ACCESS_TOKEN', token)
        setToken(token)
        setUserId(customerId)
        commit('SET_LOGIN_SOURCE', 'customer')
      }

      logger.debug(
        'Create new account response OK',
        {},
        { category: 'account-recovery' }
      )

      return response
    } catch (err) {
      logger.error('Create new account failed', err, {
        category: 'account-recovery',
      })
      throw err
    }
  },

  async getAccountRecoveryStatus({ commit, rootGetters }) {
    try {
      const checkoutId = rootGetters['session/checkoutId']
      logger.debug(
        'Get account recovery status request',
        {},
        { category: 'account-recovery' }
      )

      const response = await AccountRecoveryAPI.getAccountRecoveryStatus(
        checkoutId
      )

      const { status, data: dataResponse } = response.data || {}
      const maskedNationalId = dataResponse?.maskedNationalId

      if (maskedNationalId && status === 'SUCCESS') {
        commit('SET_MASKED_NATIONAL_ID', maskedNationalId)
      } else if (status === 'MAX_ATTEMPTS_REACHED') {
        // Max attempts reached, show full screen error
        commit(
          'SET_MERCHANT_FAILURE_URL',
          dataResponse?.merchantFailureUrl || '/'
        )
        commit('SET_SHOW_MAX_ATTEMPTS_SCREEN', true)
      }

      logger.debug(
        'Get account recovery status response OK',
        {},
        { category: 'account-recovery' }
      )

      return response
    } catch (error) {
      logger.error('Get account recovery status failed', error, {
        category: 'account-recovery',
      })
      throw error
    }
  },
  setShowMaxAttemptsScreen({ commit }, value) {
    commit('SET_SHOW_MAX_ATTEMPTS_SCREEN', value)
  },
  setMerchantFailureUrl({ commit }, value) {
    commit('SET_MERCHANT_FAILURE_URL', value)
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
